import { z } from 'zod';

/**
 * Spanish messages for every Zod issue that has no message of its own. Schemas that pass a
 * custom message (`.min(1, 'Indica el motivo…')`, refinements) keep it: Zod only calls the
 * error map when the issue has no explicit message. Loaded once from `main.tsx`; the shared
 * schemas and the web app resolve to the same Zod instance, so the map applies to both.
 */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const zodErrorMapEs: z.ZodErrorMap = (issue) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: 'Este campo es obligatorio.' };
      }
      if (issue.expected === 'number' || issue.expected === 'integer' || issue.received === 'nan') {
        return { message: 'Ingresa un número válido.' };
      }
      if (issue.expected === 'string') return { message: 'Ingresa un texto válido.' };
      if (issue.expected === 'boolean') return { message: 'Indica sí o no.' };
      return { message: 'El valor no es válido.' };

    case z.ZodIssueCode.invalid_string:
      if (issue.validation === 'email') return { message: 'Ingresa un correo electrónico válido.' };
      if (issue.validation === 'url') return { message: 'Ingresa una dirección web válida.' };
      if (issue.validation === 'datetime') return { message: 'Ingresa una fecha y hora válidas.' };
      return { message: 'El formato no es válido.' };

    case z.ZodIssueCode.too_small: {
      const min = Number(issue.minimum);
      if (issue.type === 'string') {
        return {
          message:
            min <= 1
              ? 'Este campo es obligatorio.'
              : `Debe tener al menos ${plural(min, 'carácter', 'caracteres')}.`,
        };
      }
      if (issue.type === 'array') {
        return { message: `Agrega al menos ${plural(min, 'elemento', 'elementos')}.` };
      }
      if (issue.type === 'number' || issue.type === 'bigint') {
        return {
          message: issue.inclusive
            ? `Debe ser ${min === 0 ? 'cero o más' : `mayor o igual a ${min}`}.`
            : `Debe ser mayor que ${min}.`,
        };
      }
      return { message: 'El valor es demasiado bajo.' };
    }

    case z.ZodIssueCode.too_big: {
      const max = Number(issue.maximum);
      if (issue.type === 'string') {
        return { message: `No puede superar ${plural(max, 'carácter', 'caracteres')}.` };
      }
      if (issue.type === 'array') {
        return { message: `No puede tener más de ${plural(max, 'elemento', 'elementos')}.` };
      }
      if (issue.type === 'number' || issue.type === 'bigint') {
        return {
          message: issue.inclusive ? `No puede ser mayor que ${max}.` : `Debe ser menor que ${max}.`,
        };
      }
      return { message: 'El valor es demasiado alto.' };
    }

    case z.ZodIssueCode.invalid_enum_value:
    case z.ZodIssueCode.invalid_literal:
    case z.ZodIssueCode.invalid_union_discriminator:
      return { message: 'Selecciona una opción válida.' };

    case z.ZodIssueCode.invalid_date:
      return { message: 'Ingresa una fecha válida.' };

    case z.ZodIssueCode.not_multiple_of:
      return { message: `Debe ser múltiplo de ${issue.multipleOf}.` };

    case z.ZodIssueCode.not_finite:
      return { message: 'Ingresa un número válido.' };

    case z.ZodIssueCode.custom:
      return { message: issue.message ?? 'El valor no es válido.' };

    default:
      return { message: 'El valor no es válido.' };
  }
};

z.setErrorMap(zodErrorMapEs);
